/**
 * identity-service client (otter-camp-suite).
 *
 * Mirrors dao-suite's `identity-service-client` so a logged-in DAO member who
 * navigates to Otter Camp stays authenticated. All DAO suites share the
 * `.helloworlddao.com` registrable domain, and the portal authenticates via
 * identity-service (in-memory JWT + the httpOnly `hwdao_refresh` cookie,
 * Domain=.helloworlddao.com — so the cookie IS sent to
 * `ottercamp.staging.helloworlddao.com`). This module owns the credentialed
 * XHRs the suite makes to the identity-service:
 *
 *  1. {@link exchangeCodeForJwt} — on a `?code=` login return, POST
 *     `/token/exchange {code}` (credentialed) → `{jwt}`.
 *  2. {@link refreshIdentityJwt} — silent rehydrate: POST `/token/refresh` with
 *     `credentials: 'include'` and NO body (the httpOnly cookie carries the
 *     refresh token) → fresh `{jwt}` + rotated cookie. This is the call that
 *     rehydrates the cross-suite session on a fresh load.
 *  3. {@link logoutIdentitySession} — POST `/logout` (credentialed) to clear the
 *     refresh cookie + revoke the token family.
 *
 * **Backward-compatible.** None of this forces existing cookie sessions to log
 * out. The suite keeps working on the legacy oracle-bridge cookie path; it
 * merely *also* obtains an identity-service JWT once the refresh/exchange flow
 * runs.
 *
 * The identity-service base + the hosted-login-front-door base are env-driven
 * (`VITE_IDENTITY_SERVICE_URL`, `VITE_LOGIN_FRONTDOOR_URL`) so staging/prod
 * differ by config, not code.
 */

/** Default identity-service base (staging) when the env var is unset. */
const DEFAULT_IDENTITY_SERVICE_URL = 'https://identity.staging.helloworlddao.com';

/** Default hosted-login-front-door base (staging) when the env var is unset. */
const DEFAULT_LOGIN_FRONTDOOR_URL = 'https://register.staging.helloworlddao.com';

/** The query parameter the hosted login UI reads for the cross-domain handoff. */
export const RETURN_ORIGIN_PARAM = 'return_origin';

/** The query parameter the front-door redirect carries the one-time code in. */
export const CODE_PARAM = 'code';

/** identity-service base URL, trailing slash stripped. */
export function getIdentityServiceUrl(): string {
  const raw = import.meta.env.VITE_IDENTITY_SERVICE_URL || DEFAULT_IDENTITY_SERVICE_URL;
  return raw.replace(/\/+$/, '');
}

/** Hosted-login-front-door base URL, trailing slash stripped. */
export function getLoginFrontdoorUrl(): string {
  const raw = import.meta.env.VITE_LOGIN_FRONTDOOR_URL || DEFAULT_LOGIN_FRONTDOOR_URL;
  return raw.replace(/\/+$/, '');
}

/**
 * This suite's own served origin — the `return_origin` the front-door redirects
 * back to after authentication. Using `window.location.origin` (rather than a
 * hard-coded env value) guarantees the origin is the exact host serving the
 * page, which is also what identity-service's `ALLOWED_RETURN_ORIGINS` +
 * credentialed CORS allowlist are keyed on.
 */
export function getSuiteOrigin(): string {
  return window.location.origin;
}

/**
 * Build the hosted-login-front-door URL to redirect an unauthenticated member
 * to. The front-door authenticates the user, then redirects back to
 * `<return_origin>?code=<code>`.
 */
export function buildLoginFrontdoorUrl(): string {
  const url = new URL(`${getLoginFrontdoorUrl()}/login`);
  url.searchParams.set(RETURN_ORIGIN_PARAM, getSuiteOrigin());
  return url.toString();
}

/** Shape of the identity-service `/token/exchange` + `/token/refresh` success body. */
interface JwtResponse {
  jwt: string;
}

/**
 * Exchange a one-time front-door code for an identity-service access JWT.
 *
 * POSTs `/token/exchange {code}` CREDENTIALED (so identity-service can set the
 * httpOnly `hwdao_refresh` cookie on the response). Returns the JWT on success,
 * or `null` on any failure (invalid/used/expired code, network error) — callers
 * fall back to the existing cookie session rather than hard-breaking.
 */
export async function exchangeCodeForJwt(code: string): Promise<string | null> {
  try {
    const res = await fetch(`${getIdentityServiceUrl()}/token/exchange`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as JwtResponse | null;
    return data?.jwt && data.jwt.length > 0 ? data.jwt : null;
  } catch {
    return null;
  }
}

/**
 * Silently refresh the identity-service access JWT using the httpOnly refresh
 * cookie. This is the cross-suite rehydrate: a DAO member authenticated at the
 * portal carries the shared `hwdao_refresh` cookie, so on a fresh Otter Camp
 * load this mints a fresh access JWT for the same session.
 *
 * POSTs `/token/refresh` with `credentials: 'include'` and NO body — the cookie
 * carries the refresh token; identity-service rotates it (fresh cookie) and
 * mints a new access JWT. Returns the fresh JWT, or `null` when refresh fails
 * (expired / revoked / reused / no cookie) so the caller proceeds
 * unauthenticated.
 */
export async function refreshIdentityJwt(): Promise<string | null> {
  try {
    const res = await fetch(`${getIdentityServiceUrl()}/token/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as JwtResponse | null;
    return data?.jwt && data.jwt.length > 0 ? data.jwt : null;
  } catch {
    return null;
  }
}

/**
 * Revoke the identity-service refresh-token family + clear the httpOnly cookie.
 * Best-effort: a failure does not block the rest of the logout flow.
 */
export async function logoutIdentitySession(): Promise<void> {
  try {
    await fetch(`${getIdentityServiceUrl()}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort — the caller proceeds with the local clear + redirect.
  }
}
